import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers, faComments, faChalkboardTeacher, faUserFriends, faClipboardList, faBusinessTime } from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as CommonFileTextClock } from "./assets/svgs/common-file-text-clock.svg";
import { ReactComponent as ELearningMonitor } from "./assets/svgs/e-learning-monitor.svg";
import { ReactComponent as Calendar3 } from "./assets/svgs/calendar-3.svg";
import { ReactComponent as FolderStar } from "./assets/svgs/folder-star.svg";
import { ReactComponent as GroupIcon } from "./assets/svgs/multiple-neutral-2.svg";
import StreamlineIconWrapper from "./common/components/StreamlineIconWrapper";
import { idArrayToDict } from "@amplio-common/funcs/commonFunctionalities";
import { Trans } from "@lingui/macro";

export const SECOND = 1000;
export const MINUTE = SECOND * 60;

export const FEATURE_FLAGS_LOADING_TOLERANCE = MINUTE * 5;

export const NEW_VERSION_CHECK_INTERVAL = MINUTE * 3;
export const MAX_PROCESS_REPORT_PERIOD_MONTHS = 6;

export const SESSION_TIMEOUT = MINUTE * 15;
export const SESSION_TIMEOUT_STUDENTS = MINUTE * 30;

export const IS_PRD = window.location.hostname === "clinic.poppins.co.il";
export const IS_DEV_OR_STG = window.location.href.includes("poppins.live");
export const IS_DEV = window.location.href.includes("localhost");

export const ROLES = {
  UNVERIFIED: { id: 0, title: "Unverified" },
  STUDENT: { id: 1, title: "Student" },
  TRAINER: { id: 10, title: "Practice Buddy" },
  THERAPIST: { id: 20, title: "Interventionist" },
  CLINIC_ADMINISTRATOR: { id: 50, title: "School Administrator" },
  CLINICAL_SUPERVISOR: { id: 40, title: "Clinical Supervisor" },
  SPED: { id: 65, title: "District Supervisor" },
  TECH_SUPPORT: { id: 90, title: "Tech Support" },
  SYSTEM_ADMIN: { id: 100, title: "System Admin" },
};

export const CONSENTS = {
  NOT_GIVEN_CONSENT: { id: 0, title: "Not Given" },
  GOOD_CONSENT: { id: 1, title: "Good" },
  BLOCKED_CONSENT: { id: 2, title: "Blocked" },
  NO_CONSENTED_STATUS: 0,
  CONSENTED_STATUS: 1,
};

export const TREATMENT_STATUSES = {
  SCHEDULED: { id: 0, title: "Scheduled" },
  DONE: { id: 1, title: "Done" },
  MISSED: { id: 2, title: "Missed" },
  CANCELLED: { id: 3, title: "Cancelled" },
};

export const TREATMENT_TYPES = {
  THERAPY: { id: 1, title: "Supervised session", defaultDuration: 45, isDirect: true, visibleForTherapist: true, isNew: false, isOnSite: false, icon: <FontAwesomeIcon icon={faComments} className="icon-type icon-type-therapy" />, treatmentPath: "treatment" },
  PRACTICE: { id: 2, title: "Practice", defaultDuration: 10, isDirect: true, visibleForTherapist: false, isNew: false, isOnSite: false, icon: <FontAwesomeIcon icon={faUserFriends} className="icon-type icon-type-practice" />, treatmentPath: "treatment" }, // LEGACY - not in use
  SELF_PRACTICE: { id: 3, title: "Self practice", defaultDuration: 10, isDirect: false, treatmentPath: "treatment" }, // LEGACY - not in use
  GROUP: { id: 7, title: "Group session", defaultDuration: 45, isDirect: true, visibleForTherapist: false, isNew: false, isOnSite: false, icon: <FontAwesomeIcon icon={faUsers} className="icon-type icon-type-group" />, treatmentPath: "treatment" },
  CLASS: { id: 8, title: "Session", defaultDuration: 45, isDirect: true, visibleForTherapist: true, isNew: false, isOnSite: true, icon: <FontAwesomeIcon icon={faChalkboardTeacher} className="icon-type icon-type-class" />, treatmentPath: "treatment/v3" },
  V4_SESSION_PILOT: { id: 9, title: "Session", defaultDuration: 30, isDirect: true, visibleForTherapist: true, isNew: false, isOnSite: true, icon: <FontAwesomeIcon icon={faChalkboardTeacher} className="icon-type icon-type-class" />, treatmentPath: "treatment/v4" },
  DEMO: { id: 10, title: "Demo session", defaultDuration: 45, isDirect: true, visibleForTherapist: true, isNew: true, isOnSite: true, icon: <FontAwesomeIcon icon={faClipboardList} className="icon-type icon-type-class" />, treatmentPath: "treatment/v3" },
  GROUP_CLASS: { id: 11, title: "New Group Session", defaultDuration: 45, isDirect: true, visibleForTherapist: false, isNew: false, isOnSite: true, icon: <FontAwesomeIcon icon={faChalkboardTeacher} className="icon-type icon-type-class" />, treatmentPath: "treatment/v3" },
  GROUP_DEMO: { id: 12, title: "Group Demo Session", defaultDuration: 45, isDirect: true, visibleForTherapist: false, isNew: false, isOnSite: true, icon: <FontAwesomeIcon icon={faChalkboardTeacher} className="icon-type icon-type-class" />, treatmentPath: "treatment/v3" },
  MEETING: { id: 13, title: "Meeting", defaultDuration: 45, isDirect: true, visibleForTherapist: true, isNew: false, isOnSite: false, icon: <FontAwesomeIcon icon={faBusinessTime} className="icon-type icon-type-class" />, treatmentPath: "treatment/v3" },
};

export const TREATMENT_TYPES_IDS = idArrayToDict(Object.values(TREATMENT_TYPES));

export const STUDENT_SESSION_STATUS = {
  10: { title: "Not Registered", text: "User has't logged in to the system yet", isRed: true },
  20: { title: "Registration Expired", text: "User still hasn't logged in, registration notification was sent over 7 days ago", isRed: true },
  30: { title: "Waiting", text: "No future scheduled sessions", isRed: true },
  40: { title: "Scheduled Sessions", text: "At least one future session is scheduled, no 'completed' sessions yet", isRed: false },
  50: { title: "Scheduled Sessions But Not Registered", text: "User hasn't logged in and there is at least one future session scheduled", isRed: false },
  60: { title: "Completed Sessions", text: "At least one 'completed' session or 'pending' session", isRed: false },
};

export const TREATMENT_TYPES_LANG_DICT = {
  1: {
    1: "individual",
    2: "practice",
    4: "assessment",
    7: "group",
    8: "class",
  },
  2: {
    1: "individual",
    2: "practice",
    4: "assessment",
    7: "group",
    8: "class",
  },
  3: {
    1: "individual session",
    2: "practice",
    4: "assessment",
    7: "group session",
    8: "class",
  },
};

export const TREATMENT_TYPES_LABELS = Object.values(TREATMENT_TYPES).reduce((res, item) => {
  res[item.id] = item.title;
  return res;
}, {});

export const USER_GENDERS = {
  NOT_SPECIFIED_GENDER: { id: 0, title: "Not specified" },
  MALE_GENDER: { id: 1, title: "Male" },
  FEMALE_GENDER: { id: 2, title: "Female" },
  OTHER_GENDER: { id: 3, title: "Other" },
};

export const TREATMENT_DURATION_MINUTES = {
  STANDART_TREATMENT_DURATION: 30,
  DYSLEXIA_TREATMENT_DURATION: 45,
  ASSESMENT_TREATMENT_DURATION: 90,
};

export const API_CACHE_TIME = 600; // 10 minutes
export const DATETIME_FORMAT = "MMM Do YYYY, hh:mm a";
export const DATE_FORMAT = "MM/DD";
// For use with the date selector
export const DT_FORMAT = "YYYY-MM-DDTHH:mm";
// Prefix for treatment webSocket
export const TREATMENT_WS_PREFIX = "TREATMENT_WS_PREFIX";

export const GoogleAnalyticsID = "UA-149718238-1";
export const GoogleAnalyticsStagingID = "UA-149718238-2";

// MIXPANEL
export const MIXPANEL_PROD_TOKEN = "546598c36d05a649722bf0359ad4f2fe";
export const MIXPANEL_STG_TOKEN = "305384c1569f06d1e17ffc408935134c";

// SERVICE
export const SERVICE_DYSLEXIA_DB_NAME = "dyslexia";
// export const SERVICES = {
//   SPEECH_LANGUAGE: 1,
//   DYSLEXIA: 2,
// };


export const SERVICES = {
  SPEECH_LANGUAGE: { id: 1, title: "Speech/Language Therapy (SL)", visibleForTherapist: true, isNew: false },
  DYSLEXIA: { id: 2, title: "Dyslexia", visibleForTherapist: false, isNew: false },
  OCCUPATIONAL_THERAPY: { id: 3, title: "Occupational Therapy (OT)", visibleForTherapist: false, isNew: false },
};

export const SERVICES_IDS = idArrayToDict(Object.values(SERVICES));

// Dyslexia attribution program
export const DYSLEXIA_ATTRIBUTION = {
  SPECIAL_EDUCATION: "sped",
  504: "504",
  PRE_ASSESSMENT: "preAssessment",
};

// Treatment ping interval
export const TreatmentPingMSInterval = 7000;

// COURSES
export const DYSLEXIA_COURSE_ID = 5;

// WebSocket Error for authentication
export const WS_ERROR_CODES = {
  WS_UNAUTH: 3401,
  WS_PERMISSION_DENIED: 3403,
  WS_DUPLICATE: 3409,
  WS_GAIERR_CODE: 3503, //It is not 5003 or 5000 because the server didnt return thouse close codes
  WS_UNKNOWN_EXCEPTION_CODE: 3500,
  WS_RECONNECT_CODE: 3101,
};

export const DATEONLY_FORMAT = "MMM d, yyyy";

export const AMPLIO_ANALITICS_REQUEST_TYPES = {
  WORD_SUMMARY: "word_summary",
  TIME_SERIES: "time_series",
};

export const ROUTES = {
  CLASSROOM: "/treatment/v3", // BACKWARDS COMPATABILITY
  CLASSROOM_V3: "/treatment/v3",
  CLASSROOM_V4: "/treatment/v4",
  STUDENTS: "/home/student-groups",
  CALENDAR: "/home/calendar",
  TIMESHEET: "/home/reports",
  RESOURCES: "/home/resources",
  PORTFOLLO: "/home/portfollo",
  SELF_PRACTICE: "/home/self-practice",
};

export const TABS = {
  STUDENTS: "student-groups",
  CALENDAR: "calendar",
  TIMESHEET: "reports",
  RESOURCES: "resources",
};

export const THERAPIST_NAVIGATION_ITEMS = [
  { path: ROUTES.CLASSROOM_V3, name: "Classroom", icon: <StreamlineIconWrapper><ELearningMonitor /></StreamlineIconWrapper> },
  { path: ROUTES.STUDENTS, name: "Students and Groups", icon: <StreamlineIconWrapper><GroupIcon /></StreamlineIconWrapper> },
  { path: ROUTES.CALENDAR, name: "Calendar", icon: <StreamlineIconWrapper><Calendar3 /></StreamlineIconWrapper> },
  { path: ROUTES.TIMESHEET, name: "Timesheet", icon: <StreamlineIconWrapper><CommonFileTextClock /></StreamlineIconWrapper> },
  { path: ROUTES.RESOURCES, name: "Resources", icon: <StreamlineIconWrapper><FolderStar /></StreamlineIconWrapper> },
];

export const STUDENT_NAVIGATION_ITEM = { path: ROUTES.PORTFOLLO, name: <Trans>Portfollo</Trans> };

export const recurrenceDays = [
  { id: 0, label: "Sunday", initial: <Trans>Su</Trans> },
  { id: 1, label: "Monday", initial: <Trans>Mo</Trans> },
  { id: 2, label: "Tuesday", initial: <Trans>Tu</Trans> },
  { id: 3, label: "Wednesday", initial: <Trans>We</Trans> },
  { id: 4, label: "Thursday", initial: <Trans>Th</Trans> },
  { id: 5, label: "Friday", initial: <Trans>Fr</Trans> },
  { id: 6, label: "Saturday", initial: <Trans>Sa</Trans> },
];

export const recurrenceTypes = {
  DAILY: 1,
  WEEKLY: 2,
  MONTHLY: 3,
};

export const monthlyRecurrence = {
  DAY_OF_MONTH: "day_of_month",
  WEEK_OF_MONTH: "week_of_month",
};

export const TYPE_STUDENT = 1;
export const TYPE_THERAPIST = 2;
export const TYPE_SCHOOL = 3;

export const NEW_VERSION_MSG = <Trans>A newer version is available</Trans>;
export const TOAST_ID_VER_CHK = "no-duplicate-version-check";

//Vocabulary analytics
export const ARTICULATION_SOUNDS = "articulation_sounds";
export const ARTICULATION_SOUNDS_POSITIONS = "articulation_sounds_positions";
export const VOCABULARY_CATEGORIES = "vocabulary_categories";
export const NUM_OF_SYLLABLES = "num_of_syllables";
export const SYLLABLES_SHAPES = "syllables_shapes";
export const THEMES = "themes";
export const GRADES = "grades";

export const BOTTOM_PANNEL_DIALOG_Z_INDEX = 1502;
